import { useMutation } from "@apollo/client/react";
import { AttachmentUrlStatus } from "@lytra/frontend-common/__generated__/graphql";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useToast } from "@/hooks/use-toast";

import { GET_ATTACHMENT_URL_MUTATION } from "./ticketQueries";

/** How often an attachment still being fetched is asked for again. */
export const ATTACHMENT_POLL_INTERVAL_MS = 1000;
/** How long to wait for it before reporting that it could not be loaded. */
export const ATTACHMENT_POLL_TIMEOUT_MS = 30_000;

function saveAttachment(url: string, filename: string): void {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * A click on an attachment downloads it. One that is not stored yet (an outbound mail's,
 * never downloaded at ingest) is fetched from the mailbox: the server answers LOADING, the
 * attachment is asked for again every second and saved as soon as its URL comes back. After
 * ATTACHMENT_POLL_TIMEOUT_MS, or when it cannot be fetched, the click ends in an error toast.
 * One request or poll per attachment at a time; leaving the ticket stops them.
 */
export function useAttachmentDownload(): {
  downloadAttachment: (attachmentId: string) => void;
  loadingAttachmentIds: ReadonlySet<string>;
} {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [getAttachmentUrl] = useMutation(GET_ATTACHMENT_URL_MUTATION);
  const [loadingAttachmentIds, setLoadingAttachmentIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  // Attachments with a request or poll running, each with the timer of its next attempt.
  const pollsRef = useRef(
    new Map<string, ReturnType<typeof setTimeout> | null>(),
  );

  useEffect(() => {
    const polls = pollsRef.current;
    return () => {
      for (const timer of polls.values()) {
        if (timer) clearTimeout(timer);
      }
      polls.clear();
    };
  }, []);

  const setLoading = useCallback((attachmentId: string, loading: boolean) => {
    setLoadingAttachmentIds((prev) => {
      if (prev.has(attachmentId) === loading) return prev;
      const next = new Set(prev);
      if (loading) next.add(attachmentId);
      else next.delete(attachmentId);
      return next;
    });
  }, []);

  const downloadAttachment = useCallback(
    (attachmentId: string) => {
      const polls = pollsRef.current;
      if (polls.has(attachmentId)) return;
      polls.set(attachmentId, null);
      const deadline = Date.now() + ATTACHMENT_POLL_TIMEOUT_MS;

      const finish = () => {
        polls.delete(attachmentId);
        setLoading(attachmentId, false);
      };
      const notLoaded = () => {
        finish();
        toast({
          title: t("tickets.attachmentDownload.failed"),
          variant: "destructive",
        });
      };

      const attempt = async (): Promise<void> => {
        let response;
        try {
          const { data } = await getAttachmentUrl({
            variables: { attachmentId },
          });
          response = data?.conversationGetAttachmentUrl;
        } catch {
          if (!polls.has(attachmentId)) return;
          finish();
          toast({
            title: "Fehler beim Herunterladen",
            description: "Der Anhang konnte nicht heruntergeladen werden.",
            variant: "destructive",
          });
          return;
        }
        // Left the ticket meanwhile: the cleanup stopped this poll.
        if (!polls.has(attachmentId)) return;

        if (
          response?.status === AttachmentUrlStatus.Ready &&
          response.presignedUrl
        ) {
          finish();
          saveAttachment(response.presignedUrl, response.filename);
          return;
        }
        if (
          response?.status !== AttachmentUrlStatus.Loading ||
          Date.now() + ATTACHMENT_POLL_INTERVAL_MS > deadline
        ) {
          notLoaded();
          return;
        }
        setLoading(attachmentId, true);
        polls.set(
          attachmentId,
          setTimeout(() => void attempt(), ATTACHMENT_POLL_INTERVAL_MS),
        );
      };

      void attempt();
    },
    [getAttachmentUrl, setLoading, t, toast],
  );

  return { downloadAttachment, loadingAttachmentIds };
}
